import React, { useEffect, useRef, useCallback } from "react";
import { useEditor } from "../EditorContext";
import {
    LayoutDashboard,
    FileCode,
    Save,
    RotateCcw,
    Crown,
    Loader2,
} from "lucide-react";
import Editor from "@monaco-editor/react";
import { useBluelagoon } from "@bluelagoon";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@shadcn-in-nx/ui/alert";
import { useChatContext } from "../ChatContext";
import PlanRestrictionAlert from "./PlanRestrictionAlert";
import DiffEditorView from "./DiffEditorView";

const editorOptions = {
    minimap: { enabled: false },
    fontSize: 14,
    lineNumbers: "on",
    scrollBeyondLastLine: true,
    wordWrap: "off",
    folding: false,
};

const readOnlyEditorOptions = {
    ...editorOptions,
    readOnly: true,
    domReadOnly: true,
};

function ComponentCodeViewer({
    selectedComponent,
    dashboardSection,
    onComponentUpdated,
    highlightLine,
    onSaveHandlerReady,
    hasCodeEditorAccess = true, // Default to true for backwards compatibility
    isEnviromentLocked = false,
}) {
    const { selectedEnvironment } = useChatContext();
    const { t } = useTranslation();
    const {
        fileStates,
        updateFileCode,
        setOriginalCode,
        diffVersion,
        setDiffVersion,
        saveViewState,
        getViewState,
    } = useEditor();

    const editorRef = useRef(null);
    const previousComponentIdRef = useRef(null);
    const pendingHighlightLineRef = useRef(null);

    const { call: updateComponentApi, isLoading: isSaving } = useBluelagoon({
        method: "updateComponent",
    });

    // Get state from context
    const fileState = selectedComponent
        ? fileStates[selectedComponent.id]
        : null;

    const isLoadingCode = fileState?.isLoadingCode ?? false;

    // If fileState is missing (shouldn't happen if opened via context), fallback to component code
    const currentCode = fileState
        ? fileState.code
        : selectedComponent?.code || "";
    const hasChanges = fileState ? fileState.hasChanges : false;

    // Save view state when switching away from a file
    useEffect(() => {
        const prevId = previousComponentIdRef.current;
        const currentId = selectedComponent?.id;

        // If we're switching to a different file, save the previous file's view state
        if (prevId && prevId !== currentId && editorRef.current) {
            const viewState = editorRef.current.saveViewState();
            if (viewState) {
                saveViewState(prevId, viewState);
            }
        }

        previousComponentIdRef.current = currentId;
    }, [selectedComponent?.id, saveViewState]);

    // Save view state on unmount
    useEffect(() => {
        return () => {
            if (editorRef.current && previousComponentIdRef.current) {
                const viewState = editorRef.current.saveViewState();
                if (viewState) {
                    saveViewState(previousComponentIdRef.current, viewState);
                }
            }
        };
    }, [saveViewState]);

    // Update editor read-only state when environment lock or access changes
    useEffect(() => {
        if (editorRef.current) {
            const isReadOnly = !hasCodeEditorAccess || isEnviromentLocked;
            editorRef.current.updateOptions({
                readOnly: isReadOnly,
                domReadOnly: isReadOnly,
            });
        }
    }, [hasCodeEditorAccess, isEnviromentLocked]);

    // Function to apply line highlighting
    const applyLineHighlight = useCallback((editor, lineNumber) => {
        if (!editor || !lineNumber) return;

        editor.revealLineInCenter(lineNumber);
        const decorations = editor.deltaDecorations(
            [],
            [
                {
                    range: {
                        startLineNumber: lineNumber,
                        startColumn: 1,
                        endLineNumber: lineNumber,
                        endColumn: 1,
                    },
                    options: {
                        isWholeLine: true,
                        className: "highlighted-line",
                        glyphMarginClassName: "highlighted-glyph",
                    },
                },
            ]
        );

        // Remove highlight after 3 seconds
        setTimeout(() => {
            editor.deltaDecorations(decorations, []);
        }, 3000);
    }, []);

    // Handle line highlighting - store pending if editor not ready
    useEffect(() => {
        if (highlightLine) {
            if (editorRef.current) {
                // Editor is ready, apply immediately
                applyLineHighlight(editorRef.current, highlightLine);
                pendingHighlightLineRef.current = null;
            } else {
                // Editor not ready, store for later
                pendingHighlightLineRef.current = highlightLine;
            }
        }
    }, [highlightLine, applyLineHighlight]);

    // Handle editor change
    const handleEditorChange = (value) => {
        if (selectedComponent) {
            updateFileCode(selectedComponent.id, value || "");
        }
    };

    // Handle save
    const handleSave = useCallback(async () => {
        if (!selectedComponent || !hasChanges) return;

        try {
            const response = await updateComponentApi({
                id: selectedComponent.id,
                code: currentCode,
                environmentName: selectedEnvironment,
            });

            if (response?.status === 200) {
                toast.success(
                    t("componentCodeViewer.saveSuccess") ||
                        "Component saved successfully"
                );

                // Update original code in context to match saved code
                setOriginalCode(selectedComponent.id, currentCode, true);

                // Notify parent component to refresh
                if (onComponentUpdated) {
                    onComponentUpdated();
                }
            } else {
                throw new Error(
                    response?.data?.message || "Failed to save component"
                );
            }
        } catch (error) {
            console.error("Error saving component:", error);
            toast.error(
                t("componentCodeViewer.saveError") ||
                    `Failed to save: ${error.message}`
            );
        }
    }, [
        selectedComponent,
        hasChanges,
        currentCode,
        updateComponentApi,
        selectedEnvironment,
        t,
        onComponentUpdated,
        setOriginalCode,
    ]);

    // Detect language based on file extension and component properties
    const detectLanguage = (component) => {
        // Detect from file path/name
        const fileName = component.filePath || component.name || "";
        const extension = fileName.split(".").pop()?.toLowerCase();

        switch (extension) {
            case "json":
                return "json";
            case "js":
            case "jsx":
                return "javascript"; // Monaco will handle JSX syntax highlighting
            case "ts":
            case "tsx":
                return "typescript"; // Monaco will handle TSX syntax highlighting
            case "css":
                return "css";
            case "scss":
            case "sass":
                return "scss";
            case "html":
            case "htm":
                return "html";
            case "py":
                return "python";
            case "java":
                return "java";
            case "cpp":
            case "cc":
            case "cxx":
                return "cpp";
            case "c":
                return "c";
            case "php":
                return "php";
            case "rb":
                return "ruby";
            case "go":
                return "go";
            case "rs":
                return "rust";
            case "xml":
                return "xml";
            case "yaml":
            case "yml":
                return "yaml";
            case "md":
            case "markdown":
                return "markdown";
            case "sql":
                return "sql";
            case "sh":
            case "bash":
                return "shell";
            default:
                return "javascript"; // Default fallback
        }
    };

    // Handle reset
    const handleReset = () => {
        if (selectedComponent && fileState) {
            updateFileCode(selectedComponent.id, fileState.originalCode);
        }
    };

    // Expose save handler to parent for keyboard shortcuts
    useEffect(() => {
        if (onSaveHandlerReady) {
            onSaveHandlerReady(handleSave);
        }
    }, [handleSave, onSaveHandlerReady]);

    if (selectedComponent) {
        return (
            <div className="h-full flex flex-col w-full">
                {/* Header with save/reset buttons */}
                <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50">
                    <div className="flex items-center gap-2">
                        <FileCode className="w-3.5 h-3.5 text-gray-500" />
                        <span className="text-xs font-medium text-gray-700">
                            {selectedComponent.name}
                        </span>
                        {hasChanges && (
                            <span className="text-xs text-orange-600 font-medium">
                                (unsaved)
                            </span>
                        )}
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleReset}
                            disabled={
                                !hasChanges || isSaving || !hasCodeEditorAccess
                            }
                            className="flex items-center gap-1 px-2 py-1 text-xs text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title={
                                t("componentCodeViewer.reset") ||
                                "Reset changes"
                            }
                        >
                            <RotateCcw className="w-3.5 h-3.5" />
                            {t("componentCodeViewer.reset") || "Reset"}
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={
                                !hasChanges || isSaving || !hasCodeEditorAccess
                            }
                            className="flex items-center gap-1 px-2 py-1 text-xs text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            title={
                                t("componentCodeViewer.save") || "Save changes"
                            }
                        >
                            <Save className="w-3.5 h-3.5" />
                            {isSaving
                                ? t("componentCodeViewer.saving") || "Saving..."
                                : t("componentCodeViewer.save") || "Save"}
                        </button>
                    </div>
                </div>

                {/* Plan Restriction Alert */}
                {!hasCodeEditorAccess && (
                    <PlanRestrictionAlert
                        titleKey="appPreview.upgradeRequired"
                        titleFallback="Upgrade Required"
                        descriptionKey="appPreview.codeEditsUpgradeMessage"
                        descriptionFallback="In-app code editing is available on Creator plan and above. Upgrade your plan to edit code directly in the browser."
                    />
                )}
                {isEnviromentLocked && (
                    <div className="px-4 py-3 bg-yellow-50 border-b border-yellow-200">
                        <Alert className="bg-transparent border-0 px-4 py-0">
                            <Crown className="h-4 w-4 text-yellow-600" />
                            <AlertTitle className="text-yellow-800 text-sm">
                                {t(
                                    "componentCodeViewer.environmentLockedTitle"
                                ) || "Environment Locked"}
                            </AlertTitle>
                            <AlertDescription className="text-yellow-700 text-xs">
                                {t(
                                    "componentCodeViewer.environmentLockedMessage"
                                ) ||
                                    "This environment is locked. Switch to a development environment to make changes."}
                            </AlertDescription>
                        </Alert>
                    </div>
                )}

                {/* Editor */}
                <div className="flex-1 overflow-hidden relative">
                    {isLoadingCode && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/80">
                            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
                        </div>
                    )}
                    {diffVersion ? (
                        <DiffEditorView
                            originalCode={diffVersion.code || ""}
                            modifiedCode={currentCode}
                            language={detectLanguage(selectedComponent)}
                            versionLabel={
                                diffVersion.version ||
                                `v${diffVersion.id?.slice(-4) || "old"}`
                            }
                            readOnly={
                                !hasCodeEditorAccess || isEnviromentLocked
                            }
                            onRevert={() => {}}
                            onClose={() => setDiffVersion(null)}
                            onModifiedChange={(value) =>
                                updateFileCode(selectedComponent.id, value)
                            }
                            componentId={selectedComponent.id}
                            diffVersionId={diffVersion.id}
                        />
                    ) : (
                        <Editor
                            key={selectedComponent.id}
                            height="100%"
                            path={
                                selectedComponent.filePath ||
                                selectedComponent.id
                            }
                            language={detectLanguage(selectedComponent)}
                            value={currentCode}
                            onChange={handleEditorChange}
                            options={
                                hasCodeEditorAccess && !isEnviromentLocked
                                    ? editorOptions
                                    : readOnlyEditorOptions
                            }
                            theme="vs-light"
                            beforeMount={(monaco) => {
                                // Configure Monaco Editor for JSX support before mounting
                                monaco.languages.typescript.javascriptDefaults.setCompilerOptions(
                                    {
                                        jsx: monaco.languages.typescript.JsxEmit
                                            .React,
                                        jsxFactory: "React.createElement",
                                        reactNamespace: "React",
                                        allowNonTsExtensions: true,
                                        allowJs: true,
                                        target: monaco.languages.typescript
                                            .ScriptTarget.Latest,
                                    }
                                );

                                monaco.languages.typescript.javascriptDefaults.setDiagnosticsOptions(
                                    {
                                        noSemanticValidation: false,
                                        noSyntaxValidation: false,
                                    }
                                );

                                monaco.languages.typescript.typescriptDefaults.setCompilerOptions(
                                    {
                                        jsx: monaco.languages.typescript.JsxEmit
                                            .React,
                                        jsxFactory: "React.createElement",
                                        reactNamespace: "React",
                                        allowNonTsExtensions: true,
                                        allowJs: true,
                                        target: monaco.languages.typescript
                                            .ScriptTarget.Latest,
                                    }
                                );

                                monaco.languages.typescript.typescriptDefaults.setDiagnosticsOptions(
                                    {
                                        noSemanticValidation: false,
                                        noSyntaxValidation: false,
                                    }
                                );
                            }}
                            onMount={(editor) => {
                                editorRef.current = editor;

                                // Apply pending line highlight if there is one
                                if (pendingHighlightLineRef.current) {
                                    // Small delay to ensure editor is fully ready
                                    setTimeout(() => {
                                        applyLineHighlight(
                                            editor,
                                            pendingHighlightLineRef.current
                                        );
                                        pendingHighlightLineRef.current = null;
                                    }, 50);
                                } else if (selectedComponent) {
                                    // Restore view state (cursor, scroll, undo history) if available
                                    const savedViewState = getViewState(
                                        selectedComponent.id
                                    );
                                    if (savedViewState) {
                                        editor.restoreViewState(savedViewState);
                                    }
                                }
                            }}
                        />
                    )}
                </div>
            </div>
        );
    }

    if (dashboardSection === "menu") {
        return (
            <div className="flex items-center justify-center h-full text-gray-500 w-full">
                <div className="text-center">
                    <LayoutDashboard className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-sm">Select an option from the menu</p>
                </div>
            </div>
        );
    }

    if (dashboardSection === "editor") {
        return (
            <div className="flex items-center justify-center h-full text-gray-500 w-full">
                <div className="text-center">
                    <FileCode className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p className="text-sm">
                        Select a component to view its code
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="flex items-center justify-center h-full text-gray-500 w-full">
            <div className="text-center">
                <FileCode className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                <p className="text-sm">Select a component to view its code</p>
            </div>
        </div>
    );
}

export default ComponentCodeViewer;
